/* ================================================================
   AI Search — Niimbot SG
   Drives the Search ↔ Ask AI mode toggle, AI conversation,
   product recommendations and the results-page AI summary.

   Configuration:
     window.AI_SEARCH_ENDPOINT          — POST URL of your secure AI proxy.
     window.AI_SEARCH_PROVIDER_BASE_URL — OpenAI-compatible provider base URL.
     window.AI_SEARCH_MODEL             — Provider model identifier.
     window.AI_SEARCH_STORE             — Optional store name shown in replies.
     window.AI_SEARCH_DEBUG             — Set false to silence diagnostic logs.
   If the endpoint is not set, falls back to a mock response that uses
   live predictive-search products so the UI still demos correctly.
   ================================================================ */

(function () {
  'use strict';

  var STORE_NAME = window.AI_SEARCH_STORE || 'labelcraft AI';
  var ENDPOINT = window.AI_SEARCH_ENDPOINT || null;
  var PROVIDER_BASE_URL = window.AI_SEARCH_PROVIDER_BASE_URL || '';
  var PROVIDER_MODEL = window.AI_SEARCH_MODEL || '';
  var DEBUG = window.AI_SEARCH_DEBUG !== false;
  var MODE_KEY = 'sr-mode';
  var SUMMARY_KEY = 'sr-ai-summary-dismissed';
  var recommendedCombinations = {};

  /* ─────────────────────────────────────────────────────────────
     Utilities
     ───────────────────────────────────────────────────────────── */
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fmtMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var v = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return 'S$' + v;
  }

  // Shopify's /search/suggest.json returns price as a formatted string like
  // "$31.00". Convert any incoming price (number or string) to a numeric
  // cents value so totals/subtractions don't end up as NaN.
  function priceToCents(p) {
    if (typeof p === 'number' && !isNaN(p)) return p;
    if (typeof p !== 'string') return 0;
    var n = parseFloat(p.replace(/[^0-9.]/g, ''));
    if (isNaN(n)) return 0;
    return Math.round(n * 100);
  }

  function getCard() {
    return document.querySelector('[data-sr-card]');
  }

  function dbg() {
    if (!DEBUG || !window.console || !console.log) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[AI Search][Debug]');
    console.log.apply(console, args);
  }

  function noteFallback(reason, meta) {
    var detail = {
      reason: reason,
      meta: meta || {},
      at: new Date().toISOString()
    };
    window.__AI_SEARCH_LAST_FALLBACK__ = detail;
    dbg('Fallback -> mock:', detail);
  }

  function getInput() {
    return document.querySelector('#sr-overlay [data-sr-input]');
  }

  function clearAIComposer() {
    var input = getInput();
    if (!input) return;
    input.value = '';
    input.dispatchEvent(new Event('input', { bubbles: true }));
    if (input.setSelectionRange) input.setSelectionRange(0, 0);
  }

  function openOverlay() {
    var overlay = document.getElementById('sr-overlay');
    if (!overlay) return;
    if (!overlay.classList.contains('is-open')) {
      overlay.style.display = 'flex';
      overlay.classList.add('is-open');
      overlay.removeAttribute('aria-hidden');
      document.body.classList.add('sr-overlay-open');
    }
  }

  function closeOverlayForCart() {
    var overlay = document.getElementById('sr-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.display = '';
    document.body.classList.remove('sr-overlay-open');
  }

  /* ─────────────────────────────────────────────────────────────
     Mode toggle (Search ↔ Ask AI)
     ───────────────────────────────────────────────────────────── */
  function readMode() {
    try { return localStorage.getItem(MODE_KEY) || 'search'; }
    catch (_) { return 'search'; }
  }

  function writeMode(m) {
    try { localStorage.setItem(MODE_KEY, m); } catch (_) {}
  }

  function applyMode(mode) {
    var card = getCard();
    if (!card) return;
    card.dataset.mode = mode;

    document.querySelectorAll('[data-sr-mode]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.dataset.srMode === mode ? 'true' : 'false');
    });

    var input = getInput();
    if (input) {
      input.placeholder = mode === 'ai'
        ? 'Describe what you want to label, organize, or gift…'
        : 'Search products, templates, guides…';
    }

    // Swap the header icon (search ↔ sparkle)
    var hdrIcon = card.querySelector('[data-sr-header-icon]');
    if (hdrIcon) {
      hdrIcon.innerHTML = mode === 'ai'
        ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275Z"/></svg>'
        : '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>';
    }
  }

  function switchMode(mode) {
    writeMode(mode);
    applyMode(mode);

    // When switching to AI, refocus + clear predictive open state if any
    if (mode === 'ai') {
      var ps = document.querySelector('#sr-overlay predictive-search');
      if (ps) {
        ps.removeAttribute('open');
        ps.removeAttribute('loading');
      }
    }
    var input = getInput();
    if (input) {
      try { input.focus(); } catch (_) {}
    }
  }

  /* ─────────────────────────────────────────────────────────────
     AI request — calls configured endpoint, falls back to mock
     ───────────────────────────────────────────────────────────── */
  // Cached catalog snapshot — /products.json is the same response for every
  // shopper, so fetching it once per page-load is enough.
  var catalogCache = null;
  var catalogPromise = null;

  function normalizeProductVariant(v) {
    var image = v && v.featured_image;
    return {
      id: v.id,
      title: v.title || 'Default Title',
      available: !!v.available,
      price: v.price || '',
      image: (typeof image === 'string' ? image : (image && (image.src || image.url))) || ''
    };
  }

  function normalizeStorefrontProduct(p) {
    var variants = p.variants || [];
    var firstVariant = variants[0] || {};
    var availableVariants = variants.filter(function (v) { return v.available; }).map(normalizeProductVariant);
    var variant = availableVariants[0] || null;
    var img = (p.images && p.images[0]) || null;
    var bodyText = (p.body_html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    var desc = bodyText.slice(0, 160);
    return {
      id: p.id,
      variantId: variant ? variant.id : null,
      variants: availableVariants,
      handle: p.handle,
      title: p.title,
      url: '/products/' + p.handle,
      image: (variant && variant.image) || (typeof img === 'string' ? img : (img ? img.src : '')),
      // /products.json gives price as a string like "31.00" — keep as-is,
      // priceToCents() handles both numbers and strings.
      price: (variant || firstVariant).price || '',
      vendor: p.vendor || '',
      type: p.product_type || '',
      desc: desc,
      description: desc,
      compatibilityText: [p.title || '', p.handle || '', p.product_type || '', bodyText].join(' ')
    };
  }

  function fetchCatalogSnapshot() {
    if (catalogCache) return Promise.resolve(catalogCache);
    if (catalogPromise) return catalogPromise;
    catalogPromise = (async function () {
      try {
        var res = await fetch('/products.json?limit=250');
        if (!res.ok) {
          console.warn('[AI Search] /products.json returned', res.status);
          return [];
        }
        var data = await res.json();
        var arr = Array.isArray(data.products) ? data.products : [];
        catalogCache = arr.map(normalizeStorefrontProduct);
        return catalogCache;
      } catch (e) {
        console.error('[AI Search] fetchCatalogSnapshot failed:', e);
        return [];
      }
    })();
    return catalogPromise;
  }

  async function fetchPredictiveProducts(query) {
    var searchUrl = (window.routes && window.routes.predictive_search_url) || '/search/suggest';
    try {
      var res = await fetch(
        searchUrl +
        '?q=' + encodeURIComponent(query) +
        '&resources[type]=product&resources[limit]=8&section_id=predictive-search'
      );
      if (!res.ok) return [];
      var html = await res.text();
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var jsonEl = doc.getElementById('sr-product-context');
      if (!jsonEl) return [];
      var products = JSON.parse(jsonEl.textContent.trim());
      return Array.isArray(products) ? products : [];
    } catch (e) {
      console.error('[AI Search] fetchPredictiveProducts failed:', e);
      return [];
    }
  }

  async function enrichProductVariants(product) {
    if (product && Array.isArray(product.variants) && product.variants.length) return product;
    var handle = product && (product.handle || productHandleFromUrl(product.url));
    if (!handle) return product;
    try {
      var res = await fetch('/products/' + handle + '.js');
      if (!res.ok) return product;
      return normalizeStorefrontProduct(await res.json());
    } catch (_) {
      return product;
    }
  }

  // Ask-AI queries are natural-language sentences ("Gift for my mum, budget
  // S$80, she likes baking") whose words almost never appear in product
  // titles, so /search/suggest token-matching comes back empty and the AI has
  // nothing to recommend. Pull the storefront catalog instead and let the LLM
  // do the semantic match — it is far better at that than Shopify's
  // predictive index. Fall back to predictive search only if /products.json
  // is unavailable (e.g. disabled by the store).
  async function fetchProductContext(query, preferSearchMatches) {
    if (preferSearchMatches) {
      var matchingProducts = await fetchPredictiveProducts(query);
      if (matchingProducts && matchingProducts.length) {
        return await Promise.all(matchingProducts.map(enrichProductVariants));
      }
    }
    var catalog = await fetchCatalogSnapshot();
    if (catalog && catalog.length) return catalog;
    return await fetchPredictiveProducts(query);
  }

  async function callAIEndpoint(query, products, history, shopperQuery, pinnedProducts) {
    if (!ENDPOINT) {
      noteFallback('endpoint_not_configured', {
        query: query,
        productsCount: (products || []).length
      });
      return null;
    }
    var endpointProducts = prepareEndpointProducts(shopperQuery || query, products, pinnedProducts);
    dbg('POST endpoint', ENDPOINT, {
      query: query,
      providerBaseUrl: PROVIDER_BASE_URL,
      model: PROVIDER_MODEL,
      productsCount: endpointProducts.length,
      products: endpointProducts
    });
    var res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: query,
        shopperQuery: shopperQuery || query,
        products: endpointProducts,
        history: history || [],
        store: STORE_NAME,
        provider: {
          baseUrl: PROVIDER_BASE_URL,
          model: PROVIDER_MODEL
        }
      })
    });
    if (!res.ok) {
      var errorBody = await res.text().catch(function () { return ''; });
      noteFallback('endpoint_http_error', {
        status: res.status,
        statusText: res.statusText,
        body: errorBody.slice(0, 500)
      });
      var httpError = new Error('AI endpoint returned ' + res.status);
      httpError._aiFallbackNoted = true;
      throw httpError;
    }
    var data = await res.json();
    // If the response looks like an error object or has no usable content,
    // return null so the caller falls through to the mock response.
    if (!data || data.error || (!data.text && !data.products)) {
      noteFallback('endpoint_empty_payload', { payload: data });
      return null;
    }
    dbg('Endpoint response', data);
    return data;
  }



  function tokenize(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter(function (w) { return w && w.length > 1; });
  }

  function responseLanguageForQuery(query) {
    var text = String(query || '');
    if (/[\u3400-\u9fff]/.test(text)) return 'zh';
    if (/\b(saya|mahu|untuk|boleh|cari|sesuai|pencetak|pelekat|botol|sekolah|harga|murah)\b/i.test(text)) return 'ms';
    return 'en';
  }

  function languageDirectiveForQuery(query) {
    var language = responseLanguageForQuery(query);
    if (language === 'zh') {
      return 'Reply in Chinese because the shopper wrote in Chinese. Keep product names and model codes exactly as listed in the catalog.';
    }
    if (language === 'ms') {
      return 'Reply in Bahasa Melayu because the shopper wrote in Malay. Keep product names and model codes exactly as listed in the catalog.';
    }
    return 'Reply in English only because the shopper wrote in English. Do not include Chinese characters or mix in another language. Keep product names and model codes exactly as listed in the catalog.';
  }

  function localizedRecommendationCopy(language) {
    if (language === 'zh') {
      return {
        bestMatch: '最佳匹配',
        compatibleLabel: '兼容标签纸',
        bundleTitle: '组合套装',
        bundleSavings: function (count, saving) { return count + ' 件商品 · 比单独购买节省 ' + saving; }
      };
    }
    if (language === 'ms') {
      return {
        bestMatch: 'PADANAN TERBAIK',
        compatibleLabel: 'LABEL SERASI',
        bundleTitle: 'Set Permulaan',
        bundleSavings: function (count, saving) { return count + ' item · jimat ' + saving + ' berbanding beli berasingan'; }
      };
    }
    return {
      bestMatch: 'BEST MATCH',
      compatibleLabel: 'COMPATIBLE LABEL',
      bundleTitle: 'Compatible Bundle',
      bundleSavings: function (count, saving) { return count + ' items · save ' + saving + ' vs. buying separately'; }
    };
  }

  function scoreProductForQuery(query, p) {
    var qTokens = tokenize(query);
    if (!qTokens.length || !p) return 0;

    var hayTitle = String(p.title || '').toLowerCase();
    var hayType = String(p.type || '').toLowerCase();
    var hayDesc = String(p.description || p.desc || '').toLowerCase();
    var score = 0;

    qTokens.forEach(function (t) {
      if (hayTitle.indexOf(t) !== -1) score += 5;
      if (hayType.indexOf(t) !== -1) score += 3;
      if (hayDesc.indexOf(t) !== -1) score += 2;

      // Lightweight intent mapping for common Ask-AI prompts.
      if ((t === 'gift' || t === 'mum' || t === 'mom' || t === 'present') && /printer|bundle|starter/.test(hayTitle + ' ' + hayDesc)) score += 3;
      if ((t === 'baking' || t === 'kitchen' || t === 'food' || t === 'pantry') && /waterproof|sticker|label/.test(hayTitle + ' ' + hayDesc)) score += 3;
      if ((t === 'budget' || t === 'cheap' || t === 'price') && /printer|sticker|roll/.test(hayTitle + ' ' + hayDesc)) score += 1;
    });

    return score;
  }

  function productSearchText(product) {
    return String([
      product && product.title,
      product && product.handle,
      product && product.type,
      product && product.compatibilityText,
      product && product.description,
      product && product.desc
    ].join(' ')).toLowerCase();
  }

  function productIdentityText(product) {
    return String([
      product && product.title,
      product && product.handle,
      product && product.type
    ].join(' ')).toLowerCase();
  }

  function isPrinterProduct(product) {
    var identity = productIdentityText(product);
    if (/\bribbon\b|sticker|refill|paper|roll|tape/.test(identity)) return false;
    return /\blabel maker\b|\blabel makers\b|\blabel printer\b/.test(identity);
  }

  function isLabelSupplyProduct(product) {
    var type = String((product && product.type) || '').toLowerCase();
    return !isPrinterProduct(product) && /label stickers|sticker/.test(type + ' ' + productSearchText(product));
  }

  var MODEL_PATTERNS = [
    { id: 'b21 pro', re: /\bb21[\s-]*pro\b/i },
    { id: 'b2 pro', re: /\bb2[\s-]*pro\b/i },
    { id: 'b1 pro', re: /\bb1[\s-]*pro\b/i },
    { id: 'd110', re: /\bd110\b/i },
    { id: 'd101', re: /\bd101\b/i },
    { id: 'b21', re: /\bb21\b/i },
    { id: 'b31', re: /\bb31\b/i },
    { id: 'h1s', re: /\bh1s\b/i },
    { id: 'b18', re: /\bb18\b/i },
    { id: 'd11', re: /\bd11\b/i },
    { id: 'b4', re: /\bb4\b/i },
    { id: 'k3', re: /\bk3\b/i },
    { id: 'm3', re: /\bm3\b/i },
    { id: 'm2', re: /\bm2\b/i },
    { id: 'n1', re: /\bn1\b/i },
    { id: 'b2', re: /\bb2\b/i },
    { id: 'b1', re: /\bb1\b/i }
  ];

  function canonicalModelId(modelId) {
    if (modelId === 'b18') return 'n1';
    return modelId || '';
  }

  function modelPatternForId(modelId) {
    var canonical = canonicalModelId(modelId);
    if (!canonical) return null;
    if (canonical === 'n1') return /\b(?:n1|b18)\b/i;
    var match = MODEL_PATTERNS.find(function (model) { return canonicalModelId(model.id) === canonical; });
    return match ? match.re : null;
  }

  function printerModel(product) {
    var identity = String([(product && product.title) || '', (product && product.handle) || ''].join(' ')).toLowerCase();
    var match = MODEL_PATTERNS.find(function (model) { return model.re.test(identity); });
    if (!match) {
      match = MODEL_PATTERNS.find(function (model) { return model.re.test(productSearchText(product)); });
    }
    return match ? canonicalModelId(match.id) : '';
  }

  function requestedPrinterModel(query) {
    var text = String(query || '').toLowerCase();
    var match = MODEL_PATTERNS.find(function (model) { return model.re.test(text); });
    return match ? canonicalModelId(match.id) : '';
  }

  function responsePrinterModel(response) {
    var text = String(response && response.text || '');
    return requestedPrinterModel(text);
  }

  function labelSupportsPrinter(label, printer) {
    var modelId = printerModel(printer);
    if (!modelId) return false;
    var pattern = modelPatternForId(modelId);
    return !!(pattern && pattern.test(productSearchText(label)));
  }

  function labelPrimarySupportsPrinter(label, printer) {
    var modelId = printerModel(printer);
    if (!modelId) return false;
    var pattern = modelPatternForId(modelId);
    return !!(pattern && pattern.test(productIdentityText(label)));
  }

  function pickBestProducts(query, products, maxCount) {
    var arr = Array.isArray(products) ? products.slice() : [];
    if (!arr.length) return [];
    var scored = arr.map(function (p, idx) {
      return { p: p, idx: idx, score: scoreProductForQuery(query, p) };
    });

    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return a.idx - b.idx; // deterministic fallback
    });

    var top = scored.slice(0, maxCount || 3).map(function (x) { return x.p; });
    if (!top.length) return arr.slice(0, maxCount || 3);

    // If everything scored 0 (query unrelated to catalog tokens), avoid always
    // showing exact same first three by rotating with a stable query hash.
    var anySignal = scored[0] && scored[0].score > 0;
    if (!anySignal) {
      var h = 0;
      String(query || '').split('').forEach(function (ch) { h = (h * 31 + ch.charCodeAt(0)) >>> 0; });
      var start = arr.length ? (h % arr.length) : 0;
      var rotated = arr.slice(start).concat(arr.slice(0, start));
      return rotated.slice(0, maxCount || 3);
    }

    return top;
  }

  function rankedProducts(query, products) {
    return (products || []).map(function (product, index) {
      return { product: product, index: index, score: scoreProductForQuery(query, product) };
    }).sort(function (a, b) {
      return b.score - a.score || a.index - b.index;
    }).map(function (entry) {
      return entry.product;
    });
  }

  function productContextKey(product) {
    return String(product && (product.handle || normalizedProductPath(product.url) || product.id || product.title) || '')
      .toLowerCase()
      .trim();
  }

  function mergeProductContext(pinnedProducts, products) {
    var merged = [];
    var seen = {};
    (pinnedProducts || []).concat(products || []).forEach(function (product) {
      var key = productContextKey(product);
      if (!key || seen[key]) return;
      seen[key] = true;
      merged.push(product);
    });
    return merged;
  }

  function recentRecommendedProducts() {
    for (var i = conversation.length - 1; i >= 0; i -= 1) {
      var turn = conversation[i];
      if (turn.role === 'ai' && turn.content && Array.isArray(turn.content.products) && turn.content.products.length) {
        return turn.content.products.slice(0, 3);
      }
    }
    return [];
  }

  function prepareEndpointProducts(query, products, pinnedProducts) {
    var ranked = pickBestProducts(query, products, 12);
    return mergeProductContext(pinnedProducts, ranked).slice(0, 12).map(function (p) {
      var desc = String(p.description || p.desc || '')
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 120);

      return {
        id: p.id,
        handle: p.handle,
        title: p.title,
        url: p.url || (p.handle ? '/products/' + p.handle : ''),
        image: p.image || (p.featured_image && p.featured_image.url) || '',
        price: priceToCents(p.price),
        type: p.type || '',
        vendor: p.vendor || '',
        description: desc
      };
    });
  }

  function normalizedProductPath(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/$/, '').toLowerCase();
    } catch (_) {
      return String(url).split('?')[0].replace(/\/$/, '').toLowerCase();
    }
  }

  function productHandleFromUrl(url) {
    var path = normalizedProductPath(url);
    var match = path.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function matchCatalogProduct(product, catalog) {
    var path = normalizedProductPath(product && product.url);
    var title = String((product && product.title) || '').toLowerCase().trim();
    var handle = String((product && product.handle) || productHandleFromUrl(product && product.url) || '').toLowerCase().trim();

    return (catalog || []).find(function (candidate) {
      if (handle && String(candidate.handle || '').toLowerCase() === handle) return true;
      if (path && normalizedProductPath(candidate.url) === path) return true;
      return title && String(candidate.title || '').toLowerCase().trim() === title;
    }) || null;
  }

  function selectPreferredVariant(query, product) {
    var variants = (product && product.variants) || [];
    if (!variants.length) return null;
    var qTokens = tokenize(query);
    var best = variants[0];
    var bestScore = 0;
    variants.forEach(function (variant) {
      var title = String(variant.title || '').toLowerCase();
      var score = 0;
      qTokens.forEach(function (token) {
        if (title.indexOf(token) !== -1) score += token.length >= 3 ? 2 : 1;
      });
      if (score > bestScore) {
        best = variant;
        bestScore = score;
      }
    });
    return best;
  }

  function syncBundlePricing(response) {
    if (!response || !response.bundle || !response.products || response.products.length < 2) return response;
    var copy = localizedRecommendationCopy(response._language || 'en');
    var sum = response.products.reduce(function (total, product) {
      return total + (typeof product.price === 'number' ? product.price : priceToCents(product.price));
    }, 0);
    var bundlePrice = Math.round(sum * 0.88);
    response.bundle.price = bundlePrice;
    response.bundle.sub = copy.bundleSavings(response.products.length, fmtMoney(sum - bundlePrice));
    return response;
  }

  function hydrateProduct(product, source, query) {
    var selectedVariant = selectPreferredVariant(query, source);
    return Object.assign({}, product || {}, {
      id: source.id,
      variantId: selectedVariant ? selectedVariant.id : source.variantId,
      selectedVariantId: selectedVariant ? selectedVariant.id : source.variantId,
      selectedVariantTitle: selectedVariant ? selectedVariant.title : '',
      variants: source.variants || [],
      handle: source.handle,
      title: (product && product.title) || source.title,
      desc: (product && product.desc) || source.desc,
      type: source.type,
      compatibilityText: source.compatibilityText,
      url: source.url,
      image: (selectedVariant && selectedVariant.image) || (product && product.image) || source.image,
      price: selectedVariant ? priceToCents(selectedVariant.price) : priceToCents(source.price)
    });
  }

  function hydrateResponseProducts(response, catalog, query) {
    if (!response || !Array.isArray(response.products)) return response;
    var requestedCount = response.products.length;
    response.products = response.products.map(function (product) {
      var source = matchCatalogProduct(product, catalog);
      if (!source) {
        dbg('Omitting AI product not found in provided catalog:', product);
        return null;
      }
      return hydrateProduct(product, source, query);
    }).filter(Boolean);
    if (response.bundle && (response.products.length < 2 || response.products.length !== requestedCount)) {
      response.bundle = null;
    }
    return syncBundlePricing(response);
  }

  function constrainRecommendationProducts(response, catalog, query) {
    if (!response || !Array.isArray(response.products)) return response;
    var copy = localizedRecommendationCopy(response._language || responseLanguageForQuery(query));
    var shown = response.products;
    var requestedModelId = requestedPrinterModel(query);
    var preferredModelId = requestedModelId || responsePrinterModel(response);
    var printer = shown.find(function (product) {
      return isPrinterProduct(product) && (!preferredModelId || printerModel(product) === preferredModelId);
    });
    if (!printer) {
      var printerSource = rankedProducts(query, (catalog || []).filter(function (product) {
        return isPrinterProduct(product) && (!preferredModelId || printerModel(product) === preferredModelId);
      }))[0];
      if (printerSource) printer = hydrateProduct({ tag: copy.bestMatch }, printerSource, query);
    }
    if (!printer) {
      response.products = shown.filter(function (product) { return !isPrinterProduct(product); }).slice(0, 2);
      response.bundle = null;
      return response;
    }

    var seen = {};
    seen[printer.handle || printer.id] = true;
    var candidateLabels = shown.filter(function (product) {
      return isLabelSupplyProduct(product) && labelSupportsPrinter(product, printer);
    });
    rankedProducts(query, (catalog || []).filter(function (product) {
      return isLabelSupplyProduct(product) && labelSupportsPrinter(product, printer);
    })).forEach(function (source) {
      candidateLabels.push(hydrateProduct({ tag: copy.compatibleLabel }, source, query));
    });
    var primaryLabels = candidateLabels.filter(function (label) {
      return labelPrimarySupportsPrinter(label, printer);
    });
    var labelPool = primaryLabels.length ? primaryLabels : candidateLabels;
    var labels = labelPool.filter(function (label) {
      var key = label.handle || label.id;
      if (seen[key]) return false;
      seen[key] = true;
      return true;
    }).slice(0, 2);

    printer.tag = printer.tag || copy.bestMatch;
    labels.forEach(function (label) {
      label.tag = label.tag || copy.compatibleLabel;
    });
    response.products = [printer].concat(labels);
    if (response.products.length < 2) {
      response.bundle = null;
    } else if (!response.bundle) {
      response.bundle = { title: copy.bundleTitle, price: 0 };
    }
    dbg('Constrained recommendation set', {
      printer: printer.handle,
      model: printerModel(printer),
      requestedModel: requestedModelId,
      preferredModel: preferredModelId,
      labels: labels.map(function (label) { return label.handle; })
    });
    return syncBundlePricing(response);
  }

  function combinationKey(products) {
    var keys = (products || []).map(function (product) {
      return String(product.handle || normalizedProductPath(product.url) || product.id || product.title || '')
        .toLowerCase()
        .trim();
    }).filter(Boolean).sort();
    return keys.length ? keys.join('|') : '';
  }

  function suppressRepeatedCombination(response) {
    if (!response || !response.products || !response.products.length) return response;
    var key = combinationKey(response.products);
    if (!key) return response;
    if (recommendedCombinations[key]) {
      dbg('Repeated product combination omitted:', key);
      var repeatNotice = response._language === 'zh'
        ? ' 商品组合与上方推荐相同，您可以直接点击上方链接下单。'
        : (response._language === 'ms'
          ? ' Gabungan produk ini sama seperti cadangan di atas; anda boleh membuat pesanan melalui pautan di atas.'
          : ' This is the same product set shown above; you can order directly from the links above.');
      return Object.assign({}, response, {
        text: String(response.text || '') + repeatNotice,
        products: [],
        bundle: null
      });
    }
    recommendedCombinations[key] = true;
    return response;
  }

  function buildMockResponse(query, products) {
    var picks = pickBestProducts(query, products, 3);
    var lower = query.toLowerCase();
    var language = responseLanguageForQuery(query);
    var introBits = [];

    if (language === 'zh') {
      introBits.push('以下是适合您需求的标签机和兼容标签纸组合。');
    } else if (language === 'ms') {
      introBits.push('Berikut ialah pencetak label dan kertas label serasi yang sesuai untuk keperluan anda.');
    } else if (/wet|water|kitchen|pantry|food|oil/.test(lower)) {
      introBits.push('For wet or oily surfaces, choose <strong>waterproof PET labels</strong> rather than paper — they survive wipe-downs.');
    } else if (/gift|present/.test(lower)) {
      introBits.push('A compact <strong>Bluetooth printer</strong> bundled with starter rolls makes a great gift — under S$100 and printable from any phone.');
    } else if (/bakery|business|barcode|shop/.test(lower)) {
      introBits.push('For small-business use, look for a <strong>304 dpi</strong> printer that handles barcodes, dates and a small logo on the same label.');
    } else if (/kid|school|name/.test(lower)) {
      introBits.push('Name labels for kids should be <strong>waterproof</strong> and dishwasher-safe — paper labels peel off bottles within a week.');
    } else {
      introBits.push('Based on "' + esc(query) + '", here are our most popular picks. The first one is usually the best fit for most homes.');
    }

    var text = introBits.join(' ');
    if (picks.length) {
      text += language === 'zh' ? ' 我建议搭配：' : (language === 'ms' ? ' Padanan yang saya syorkan:' : ' Here\'s what I\'d pair:');
    }

    var followups = language === 'zh'
      ? ['这些标签防水吗？', '哪一款标签机适合这些标签纸？', '显示更便宜的选项']
      : (language === 'ms'
        ? ['Adakah label ini kalis air?', 'Pencetak mana sesuai untuk label ini?', 'Tunjukkan pilihan lebih murah']
        : ['What about freezer-safe labels?', 'Show me cheaper printers', 'Can it print Chinese characters?']);

    // Bundle savings = ~12% of sum. Always work in cents (numbers).
    var sum = picks.reduce(function (a, p) { return a + priceToCents(p.price); }, 0);
    var bundlePrice = Math.round(sum * 0.88);

    return {
      text: text,
      products: picks.map(function (p, i) {
        return {
          id: p.id,
          variantId: p.variantId,
          handle: p.handle,
          url: p.url,
          title: p.title,
          image: (p.image || (p.featured_image && p.featured_image.url) || null),
          price: priceToCents(p.price),
          tag: language === 'zh'
            ? (i === 0 ? '最佳匹配' : '兼容标签纸')
            : (language === 'ms'
              ? (i === 0 ? 'PADANAN TERBAIK' : 'LABEL SERASI')
              : (i === 0 ? 'BEST MATCH' : (i === 1 ? 'SURVIVES WIPE-DOWNS' : 'FREE WITH BUNDLE'))),
          desc: language === 'zh'
            ? '适合此需求的兼容产品。'
            : (language === 'ms'
              ? 'Produk serasi yang sesuai untuk keperluan ini.'
              : (p.description || '').replace(/<[^>]*>/g, '').slice(0, 80))
        };
      }),
      bundle: picks.length >= 2 ? {
        title: language === 'zh' ? '组合套装' : (language === 'ms' ? 'Set Permulaan' : 'Starter Bundle'),
        sub: language === 'zh'
          ? picks.length + ' 件商品 · 比单独购买节省 ' + fmtMoney(sum - bundlePrice)
          : (language === 'ms'
            ? picks.length + ' item · jimat ' + fmtMoney(sum - bundlePrice) + ' berbanding beli berasingan'
            : picks.length + ' items · save ' + fmtMoney(sum - bundlePrice) + ' vs. buying separately'),
        price: bundlePrice
      } : null,
      followups: followups,
      _mock: true
    };
  }

  /* ─────────────────────────────────────────────────────────────
     AI conversation rendering
     ───────────────────────────────────────────────────────────── */
  var conversation = []; // {role, content}

  function getBody() { return document.querySelector('[data-sr-ai-body]'); }
  function getThread() { return document.querySelector('[data-sr-ai-thread]'); }

  function renderConversation(extraMsg) {
    var thread = getThread();
    var body = getBody();
    if (!thread || !body) return;
    body.setAttribute('data-state', 'thread');

    // Force-close the predictive-search dropdown so its results don't
    // show above the AI thread (its [open] CSS otherwise wins on specificity).
    var ps = document.querySelector('#sr-overlay predictive-search');
    if (ps) {
      ps.removeAttribute('open');
      ps.removeAttribute('loading');
    }

    var html = '';
    conversation.forEach(function (turn, index) {
      if (turn.role === 'user') {
        html += renderUserMsg(turn.content);
      } else if (turn.role === 'ai') {
        html += renderAIMsg(turn.content, index);
      }
    });
    if (extraMsg) html += extraMsg;

    thread.innerHTML = html;

    // Scroll the latest message into view. The scroll container is
    // .sr-card__body (overflow-y:auto), so scrollIntoView on the last
    // element bubbles up to the right ancestor.
    requestAnimationFrame(function () {
      var last = thread.lastElementChild;
      if (last && last.scrollIntoView) {
        last.scrollIntoView({ block: 'end', behavior: 'smooth' });
      }
    });
  }

  function renderUserMsg(text) {
    return (
      '<div class="sr-ai-msg sr-ai-msg--user">' +
        '<div class="sr-ai-msg__avatar sr-ai-msg__avatar--user">u</div>' +
        '<div class="sr-ai-msg__body">' +
          '<div class="sr-ai-msg__name">You</div>' +
          '<div class="sr-ai-msg__text">' + esc(text) + '</div>' +
        '</div>' +
      '</div>'
    );
  }

  function renderThinking() {
    return (
      '<div class="sr-ai-msg">' +
        '<div class="sr-ai-msg__avatar">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275Z"/></svg>' +
        '</div>' +
        '<div class="sr-ai-msg__body">' +
          '<div class="sr-ai-msg__name">' + esc(STORE_NAME) + '</div>' +
          '<div class="sr-thinking"><span></span><span></span><span></span></div>' +
        '</div>' +
      '</div>'
    );
  }

  function isDefaultVariantTitle(title) {
    return !title || /^default title$/i.test(String(title).trim());
  }

  function renderVariantControl(product, turnIndex, productIndex) {
    var variants = Array.isArray(product.variants) ? product.variants : [];
    if (!variants.length) return '';
    var selectedId = String(product.selectedVariantId || product.variantId || '');
    if (variants.length > 1) {
      return (
        '<label class="sr-ai-product__variant">' +
          '<select class="sr-ai-product__select" data-sr-variant-select data-sr-turn-index="' + esc(turnIndex) + '" data-sr-product-index="' + esc(productIndex) + '" aria-label="Choose option for ' + esc(product.title || '') + '">' +
            variants.map(function (variant) {
              var selected = String(variant.id) === selectedId ? ' selected' : '';
              return '<option value="' + esc(variant.id) + '"' + selected + '>' + esc(variant.title) + ' - ' + esc(fmtMoney(priceToCents(variant.price))) + '</option>';
            }).join('') +
          '</select>' +
          '<span class="sr-ai-product__selection" data-sr-selected-variant>' + esc(product.selectedVariantTitle || variants[0].title) + '</span>' +
        '</label>'
      );
    }
    if (!isDefaultVariantTitle(variants[0].title)) {
      return (
        '<div class="sr-ai-product__choice">' +
          '<span>' + esc(variants[0].title) + '</span>' +
        '</div>'
      );
    }
    return '';
  }

  function renderAIMsg(payload, turnIndex) {
    var p = payload || {};
    var productsHtml = '';
    if (p.products && p.products.length) {
      productsHtml = '<div class="sr-ai-products">' +
        p.products.map(function (pr, i) {
          var tag = '<span class="sr-ai-product__tag' + (i === 0 ? '' : ' sr-ai-product__tag--neutral') + '">' +
            (pr.tag ? esc(pr.tag) : '&nbsp;') + '</span>';
          var img = pr.image
            ? '<img src="' + esc(pr.image) + '" alt="' + esc(pr.title || '') + '" loading="lazy">'
            : '';
          var priceHtml = (typeof pr.price === 'number')
            ? '<span class="sr-ai-product__price" data-sr-product-price>' + esc(fmtMoney(pr.price)) + '</span>'
            : '<span class="sr-ai-product__included">Included</span>';
          var url = pr.url || (pr.handle ? '/products/' + pr.handle : '#');
          var variantsHtml = renderVariantControl(pr, turnIndex, i);
          return (
            '<article class="sr-ai-product" data-sr-product-index="' + esc(i) + '">' +
              '<a class="sr-ai-product__media" href="' + esc(url) + '">' + img + '</a>' +
              '<div class="sr-ai-product__body">' +
                tag +
                '<h4 class="sr-ai-product__name"><a href="' + esc(url) + '">' + esc(pr.title || '') + '</a></h4>' +
                '<p class="sr-ai-product__desc">' + (pr.desc ? esc(pr.desc) : '&nbsp;') + '</p>' +
                '<div class="sr-ai-product__purchase">' +
                  variantsHtml +
                  priceHtml +
                '</div>' +
              '</div>' +
            '</article>'
          );
        }).join('') +
      '</div>';
    }

    var bundleHtml = '';
    if (p.bundle) {
      bundleHtml = (
        '<div class="sr-ai-bundle" data-sr-turn-index="' + esc(turnIndex) + '">' +
          '<div class="sr-ai-bundle__info">' +
            '<div class="sr-ai-bundle__title">' + esc(p.bundle.title || 'Bundle') + '</div>' +
            (p.bundle.sub ? '<div class="sr-ai-bundle__sub" data-sr-bundle-sub>' + esc(p.bundle.sub) + '</div>' : '') +
          '</div>' +
          '<div class="sr-ai-bundle__cta">' +
            '<span class="sr-ai-bundle__price" data-sr-bundle-price>' + esc(fmtMoney(p.bundle.price)) + '</span>' +
            '<button type="button" class="sr-ai-bundle__btn" data-sr-add-bundle data-sr-turn-index="' + esc(turnIndex) + '">' +
              'Add bundle ' +
              '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12,5 19,12 12,19"/></svg>' +
            '</button>' +
          '</div>' +
        '</div>'
      );
    }

    var followupsHtml = '';
    if (p.followups && p.followups.length) {
      followupsHtml = (
        '<div class="sr-ai-followup">' +
          '<h4 class="sr-section__title" style="margin: 8px 0;">Follow up</h4>' +
          '<div class="sr-chips">' +
            p.followups.map(function (f) {
              return '<button type="button" class="sr-chip" data-sr-ai-prompt="' + esc(f) + '">' + esc(f) + '</button>';
            }).join('') +
          '</div>' +
        '</div>'
      );
    }

    return (
      '<div class="sr-ai-msg">' +
        '<div class="sr-ai-msg__avatar">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275Z"/></svg>' +
        '</div>' +
        '<div class="sr-ai-msg__body">' +
          '<div class="sr-ai-msg__name">' + esc(STORE_NAME) + '</div>' +
          '<div class="sr-ai-msg__text">' + (p.text || '') + '</div>' +
          productsHtml +
          bundleHtml +
          followupsHtml +
        '</div>' +
      '</div>'
    );
  }

  /* ─────────────────────────────────────────────────────────────
     Main askAI flow
     ───────────────────────────────────────────────────────────── */
  async function askAI(query, searchContext) {
    query = (query || '').trim();
    if (!query) return;
    searchContext = (searchContext || '').trim();
    var requestQuery = searchContext
      ? query + '\nThe shopper came from search results for "' + searchContext +
        '". Recommend only products relevant or compatible with "' + searchContext + '".'
      : query;
    requestQuery += '\nRecommend exactly one label printer and one or two label sticker products compatible with that printer. Never recommend two printers together.';
    requestQuery += '\n' + languageDirectiveForQuery(query);

    openOverlay();
    if (readMode() !== 'ai') {
      switchMode('ai');
    } else {
      applyMode('ai');
    }

    clearAIComposer();

    conversation.push({ role: 'user', content: query });
    renderConversation(renderThinking());

    var pinnedProducts = recentRecommendedProducts();
    var products = await fetchProductContext(searchContext || query, !!searchContext);
    products = mergeProductContext(pinnedProducts, products);

    var response;
    try {
      response = await callAIEndpoint(requestQuery, products, conversation.slice(0, -1), query, pinnedProducts);
    } catch (e) {
      if (!e || !e._aiFallbackNoted) {
        noteFallback('endpoint_exception', {
          message: e && e.message ? e.message : String(e),
          name: e && e.name ? e.name : ''
        });
      }
      response = null;
    }
    if (!response) {
      response = buildMockResponse(searchContext ? query + ' for ' + searchContext : query, products);
    }
    response._language = responseLanguageForQuery(query);
    response = hydrateResponseProducts(response, products, searchContext || query);
    response = constrainRecommendationProducts(response, products, searchContext || query);
    response = suppressRepeatedCombination(response);

    conversation.push({ role: 'ai', content: response });
    renderConversation();
  }

  function resetConversation() {
    conversation = [];
    recommendedCombinations = {};
    var body = getBody();
    var thread = getThread();
    if (body) body.removeAttribute('data-state');
    if (thread) thread.innerHTML = '';
  }

  /* ─────────────────────────────────────────────────────────────
     Bundle add (multi-item add to cart)
     ───────────────────────────────────────────────────────────── */

  // Confirm the shopper-selected variant is still available before adding.
  async function resolveVariantId(product) {
    var selectedId = String(product.selectedVariantId || product.variantId || '');
    var handle = product.handle || productHandleFromUrl(product.url);
    if (!handle) return selectedId || null;
    try {
      var r = await fetch('/products/' + handle + '.js');
      if (!r.ok) return selectedId || null;
      var data = await r.json();
      var variants = data.variants || [];
      var variant = selectedId
        ? variants.find(function (v) { return String(v.id) === selectedId && v.available; })
        : variants.find(function (v) { return v.available; });
      return variant ? variant.id : null;
    } catch (_) {
      return selectedId || null;
    }
  }

  function updateSelectedVariant(select) {
    var turnIndex = parseInt(select.getAttribute('data-sr-turn-index'), 10);
    var productIndex = parseInt(select.getAttribute('data-sr-product-index'), 10);
    var turn = conversation[turnIndex];
    var product = turn && turn.role === 'ai' && turn.content.products && turn.content.products[productIndex];
    if (!product) return;
    var selectedId = String(select.value);
    var variant = (product.variants || []).find(function (option) {
      return String(option.id) === selectedId;
    });
    if (!variant) return;

    product.selectedVariantId = variant.id;
    product.variantId = variant.id;
    product.selectedVariantTitle = variant.title;
    product.price = priceToCents(variant.price);
    if (variant.image) product.image = variant.image;
    syncBundlePricing(turn.content);

    var card = select.closest('.sr-ai-product');
    var price = card && card.querySelector('[data-sr-product-price]');
    var image = card && card.querySelector('.sr-ai-product__media img');
    var selection = card && card.querySelector('[data-sr-selected-variant]');
    if (price) price.textContent = fmtMoney(product.price);
    if (image && variant.image) image.src = variant.image;
    if (selection) selection.textContent = variant.title;

    var thread = getThread();
    var bundle = thread && thread.querySelector('.sr-ai-bundle[data-sr-turn-index="' + turnIndex + '"]');
    var bundlePrice = bundle && bundle.querySelector('[data-sr-bundle-price]');
    var bundleSub = bundle && bundle.querySelector('[data-sr-bundle-sub]');
    if (bundlePrice && turn.content.bundle) bundlePrice.textContent = fmtMoney(turn.content.bundle.price);
    if (bundleSub && turn.content.bundle) bundleSub.textContent = turn.content.bundle.sub;
  }

  async function addBundleToCart(payload, btn) {
    if (!payload || !payload.products || !payload.products.length) return;

    // Button loading state
    if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

    try {
      // Resolve variant IDs for all products in parallel
      var variantIds = await Promise.all(payload.products.map(function (p) {
        return resolveVariantId(p);
      }));

      var items = variantIds
        .filter(function (id) { return id; })
        .map(function (id) { return { id: id, quantity: 1 }; });

      if (!items.length) throw new Error('No variant IDs resolved');
      if (items.length !== payload.products.length) throw new Error('One or more bundle products are unavailable');

      var cartAddUrl = ((window.routes && window.routes.cart_add_url) || '/cart/add') + '.js';
      var r = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items })
      });
      if (!r.ok) {
        var err = await r.json().catch(function () { return {}; });
        throw new Error(err.description || 'Add to cart failed');
      }

      // The cart drawer otherwise opens underneath the full-screen search overlay.
      closeOverlayForCart();

      // Refresh NbCart drawer and open it (no page navigation)
      if (window.NbCart) {
        await window.NbCart.refresh();
        window.NbCart.open();
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }

      // Reset button so user can re-add
      if (btn) { btn.disabled = false; btn.textContent = 'Added ✓'; }
      setTimeout(function () {
        if (btn) btn.textContent = 'Add bundle';
      }, 2500);

    } catch (e) {
      console.error('[AI Search] addBundleToCart error:', e);
      if (btn) {
        btn.disabled = false;
        btn.textContent = 'Could not add - try again';
        setTimeout(function () { btn.textContent = 'Add bundle'; }, 3000);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Event bindings
     ───────────────────────────────────────────────────────────── */
  document.addEventListener('click', function (e) {
    // Mode toggle
    var modeBtn = e.target.closest('[data-sr-mode]');
    if (modeBtn) {
      e.preventDefault();
      switchMode(modeBtn.dataset.srMode);
      return;
    }

    // AI prompt (suggested prompts, use case chips, follow-ups, nudge button)
    var aiTrigger = e.target.closest('[data-sr-ai-prompt]');
    if (aiTrigger) {
      e.preventDefault();
      askAI(aiTrigger.dataset.srAiPrompt, aiTrigger.dataset.srAiContext || '');
      return;
    }

    // Refine with AI button (on results page)
    var refineAI = e.target.closest('[data-sr-refine-ai]');
    if (refineAI) {
      e.preventDefault();
      var q = refineAI.dataset.srAiPrompt || '';
      askAI(q || (document.querySelector('.sr-results-hero__title') || {}).textContent || '');
      return;
    }

    // "Best printer for my home kitchen" accent chips → ai mode if marked
    var aiSuggested = e.target.closest('[data-sr-ai-suggested]');
    if (aiSuggested) {
      e.preventDefault();
      askAI(aiSuggested.dataset.srQuery || aiSuggested.textContent.trim());
      return;
    }

    // Bundle add
    var addBundle = e.target.closest('[data-sr-add-bundle]');
    if (addBundle) {
      e.preventDefault();
      if (addBundle.disabled) return;
      var turnIndex = parseInt(addBundle.getAttribute('data-sr-turn-index'), 10);
      var turn = isNaN(turnIndex) ? conversation[conversation.length - 1] : conversation[turnIndex];
      if (turn && turn.role === 'ai') addBundleToCart(turn.content, addBundle);
      return;
    }

    // Mobile close button
    var mobClose = e.target.closest('[data-sr-mobile-close]');
    if (mobClose) {
      e.preventDefault();
      var overlay = document.getElementById('sr-overlay');
      if (overlay) {
        overlay.classList.remove('is-open');
        overlay.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('sr-overlay-open');
      }
      return;
    }

    // AI summary dismiss
    var summClose = e.target.closest('[data-sr-ai-summary-close]');
    if (summClose) {
      e.preventDefault();
      var box = document.querySelector('[data-sr-ai-summary]');
      if (box) box.hidden = true;
      try { sessionStorage.setItem(SUMMARY_KEY + '-' + window.location.search, '1'); } catch (_) {}
      return;
    }
  });

  document.addEventListener('change', function (e) {
    var variantSelect = e.target.closest && e.target.closest('[data-sr-variant-select]');
    if (variantSelect) updateSelectedVariant(variantSelect);
  });

  /* AI form submit (Enter in AI mode → askAI) */
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('#sr-overlay [data-sr-form]');
    if (!form) return;
    if (readMode() === 'ai') {
      e.preventDefault();
      e.stopImmediatePropagation();
      var input = form.querySelector('[data-sr-input]');
      if (input) askAI(input.value);
    }
  });

  /* Enter / Ctrl+Enter on the search input — wired directly on the element so
     it fires before predictive-search's bubble handlers and the form submit. */
  function wireInputKeydown() {
    var srInput = document.getElementById('sr-overlay-input');
    if (!srInput || srInput._aiKeydownWired) return;
    srInput._aiKeydownWired = true;
    srInput.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;

      if (e.metaKey || e.ctrlKey) {
        // ⌘+Enter / Ctrl+Enter: always send to AI regardless of current mode
        e.preventDefault();
        e.stopImmediatePropagation();
        askAI(srInput.value);
        return;
      }

      if (e.shiftKey || e.altKey) return;

      // Plain Enter: only intercept when in AI mode
      if (readMode() !== 'ai') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      askAI(srInput.value);
    });
  }

  // Wire immediately (input is in the DOM even when overlay is hidden)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireInputKeydown);
  } else {
    wireInputKeydown();
  }

  /* Clearing or replacing draft text must retain the current AI context.
     Closing the overlay remains the boundary for a fresh session. */
  /* When the overlay closes, reset the AI thread for a fresh next-open */
  (function() {
    var overlayEl = document.getElementById('sr-overlay');
    if (!overlayEl) return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        if (m.attributeName === 'class' && !overlayEl.classList.contains('is-open')) {
          resetConversation();
        }
      });
    });
    observer.observe(overlayEl, { attributes: true, attributeFilter: ['class'] });
  }());

  /* ─────────────────────────────────────────────────────────────
     Results page: AI summary (auto-generate on load)
     ───────────────────────────────────────────────────────────── */
  async function generateResultsSummary() {
    var box = document.querySelector('[data-sr-ai-summary]');
    if (!box) return;

    var dismissedKey = SUMMARY_KEY + '-' + window.location.search;
    try {
      if (sessionStorage.getItem(dismissedKey) === '1') {
        box.hidden = true;
        return;
      }
    } catch (_) {}

    var titleEl = document.querySelector('.sr-results-hero__title em');
    var query = titleEl ? titleEl.textContent.replace(/"/g, '').trim() : '';
    if (!query) return;

    var products = await fetchProductContext(query, true);

    var response;
    try {
      response = await callAIEndpoint(
        'Give a short shopping summary for: ' + query + '\n' + languageDirectiveForQuery(query),
        products,
        [],
        query
      );
    } catch (e) {
      if (!e || !e._aiFallbackNoted) {
        noteFallback('summary_endpoint_exception', {
          message: e && e.message ? e.message : String(e),
          name: e && e.name ? e.name : ''
        });
      }
      response = null;
    }
    if (!response) response = buildMockResponse(query, products);

    var textEl = box.querySelector('[data-sr-ai-summary-text]');
    var chipsEl = box.querySelector('[data-sr-ai-summary-chips]');
    if (textEl) textEl.innerHTML = response.text || textEl.innerHTML;
    if (chipsEl && response.followups) {
      chipsEl.innerHTML = response.followups.map(function (f) {
        return '<button type="button" class="sr-chip" data-sr-ai-prompt="' + esc(f) +
          '" data-sr-ai-context="' + esc(query) + '">' +
          '<span class="sr-chip__icon"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275Z"/></svg></span>' +
          esc(f) +
          '</button>';
      }).join('');
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Bootstrap
     ───────────────────────────────────────────────────────────── */
  function init() {
    applyMode(readMode());
    if (document.querySelector('[data-sr-ai-summary]')) {
      generateResultsSummary();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
